import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Check, Loader2, Save, X } from 'lucide-react';
import type { EditorMode } from './types';

interface ClinicalNoteEditorProps {
  mode: EditorMode;
  body: string;
  createdAt?: string | null;
  isSubmitting: boolean;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  formatDateTime: (value?: string | null) => string;
}

function ClinicalNoteEditor({
  mode,
  body,
  createdAt,
  isSubmitting,
  onChange,
  onCancel,
  onSubmit,
  formatDateTime,
}: ClinicalNoteEditorProps) {
  const isCreate = mode === 'create';
  const title = isCreate ? 'Nuova nota clinica' : 'Modifica nota';
  const description = isCreate
    ? 'Salva note estese private e non condivise con il cliente.'
    : createdAt
      ? `Creata il ${formatDateTime(createdAt)}`
      : undefined;
  const submitLabel = isCreate ? 'Salva nota' : 'Salva modifiche';
  const submitIcon = isCreate ? (
    <Check className="mr-2 h-4 w-4" />
  ) : (
    <Save className="mr-2 h-4 w-4" />
  );

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          <p className="text-sm text-muted-foreground">
            Le modifiche non salvate restano su questo dispositivo finché non le salvi o le
            scarti.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            <X className="mr-2 h-4 w-4" /> Annulla
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />{' '}
                {isCreate ? 'Salvataggio…' : 'Aggiornamento…'}
              </>
            ) : (
              <>
                {submitIcon}
                {submitLabel}
              </>
            )}
          </Button>
        </div>
      </div>
      <Textarea
        value={body}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Inizia a scrivere la nota clinica..."
        className="min-h-[420px] rounded-lg border border-border/60 bg-background text-base leading-relaxed"
      />
    </section>
  );
}

export default memo(ClinicalNoteEditor);
